if (!customElements.get('atomic-collections-scroller')) {
  class AtomicCollectionsScroller extends HTMLElement {
    connectedCallback() {
      this._windowEl = this.querySelector('[data-scroller-window]');
      this._list = this.querySelector('[data-scroller-list]');
      this._items = () => Array.from(this.querySelectorAll('[data-scroller-item]'));
      this._panels = () => Array.from(this.querySelectorAll('[data-scroller-panel]'));
      this._cta = this.querySelector('[data-scroller-cta]');
      this._activeIndex = -1;
      this._listTranslateY = 0;
      this._suppressLineReleased = false;
      this._revealAnim = null;
      this._defaultCtaLabel = this.getAttribute('data-default-cta-label') || '';
      this._mobileQuery = window.matchMedia('(max-width: 989px)');

      if (!this._windowEl || !this._list) return;

      this._windowEl.classList.add('collections-scroller__list-window--suppress-line-transition');

      this._onListTransformEnd = (e) => {
        if (e.target !== this._list || e.propertyName !== 'transform') return;
        this._updateEdgeFades();
      };
      this._list.addEventListener('transitionend', this._onListTransformEnd);

      this._onResize = () => {
        this._recenterActive(false);
      };
      window.addEventListener('resize', this._onResize, { passive: true });

      this._onMobileChange = () => {
        this._updateCtaText();
        this._recenterActive(false);
      };
      this._mobileQuery.addEventListener('change', this._onMobileChange);

      this._items().forEach((btn) => {
        btn.addEventListener('click', () =>
          this._select(parseInt(btn.getAttribute('data-scroller-item'), 10), true, true)
        );
      });

      this.addEventListener('keydown', (e) => this._onKeydown(e));

      const initial = parseInt(this.getAttribute('data-initial-index') || '0', 10);
      const max = Math.max(0, this._items().length - 1);
      const clamped = Math.min(Math.max(0, initial), max);
      requestAnimationFrame(() => this._select(clamped, false, false));

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this._recenterActive(false);
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._mobileQuery && this._onMobileChange) {
        this._mobileQuery.removeEventListener('change', this._onMobileChange);
      }
      if (this._list && this._onListTransformEnd) {
        this._list.removeEventListener('transitionend', this._onListTransformEnd);
      }
      if (this._revealAnim) {
        this._revealAnim.cancel();
        this._revealAnim = null;
      }
      if (this._leaveAnim) {
        this._leaveAnim.cancel();
        this._leaveAnim = null;
      }
    }

    _onKeydown(e) {
      const items = this._items();
      if (!items.length) return;
      let next = this._activeIndex;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        next = Math.min(items.length - 1, this._activeIndex + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        next = Math.max(0, this._activeIndex - 1);
      } else {
        return;
      }
      e.preventDefault();
      this._select(next, true, true);
    }

    _select(index, focusItem = true, animateAlign = true) {
      const items = this._items();
      if (!items.length) return;
      const next = Math.min(Math.max(0, index), items.length - 1);
      if (next === this._activeIndex) return;

      const panels = this._panels();
      const oldPanel = this._activeIndex >= 0 ? panels[this._activeIndex] : null;
      const shouldAnimate = animateAlign && this._activeIndex >= 0;
      this._activeIndex = next;
      const newPanel = panels[next];

      if (this._revealAnim) {
        this._revealAnim.cancel();
        this._revealAnim = null;
      }
      if (this._leaveAnim) {
        this._leaveAnim.cancel();
        this._leaveAnim = null;
      }

      panels.forEach((p) => {
        p.classList.remove('is-leaving');
        p.style.removeProperty('clip-path');
      });

      items.forEach((btn, i) => {
        const active = i === next;
        // aria-pressed, not aria-selected — see the comment in
        // sections/collections-scroller.liquid. These are toggle buttons, not
        // listbox options, and aria-selected on them fails aria-allowed-attr.
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.classList.toggle('is-active', active);
        if (active && focusItem) btn.focus();
      });

      panels.forEach((panel, i) => {
        const active = i === next;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (shouldAnimate && newPanel && oldPanel && oldPanel !== newPanel) {
        oldPanel.classList.add('is-leaving');

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
          const styles = getComputedStyle(this);
          const raw = styles.getPropertyValue('--duration-medium').trim();
          const duration = raw.endsWith('s') && !raw.endsWith('ms')
            ? parseFloat(raw) * 1000
            : parseFloat(raw) || 300;
          const easing = styles.getPropertyValue('--ease-out-slow').trim()
            || 'cubic-bezier(0.26, 0.54, 0.32, 1)';

          /* Pin the new panel hidden before animation starts to prevent
             a single-frame flash of the fully-visible is-active clip-path. */
          newPanel.style.clipPath = 'inset(100% 0% 0% 0%)';

          this._revealAnim = newPanel.animate(
            [
              { clipPath: 'inset(100% 0% 0% 0%)' },
              { clipPath: 'inset(0% 0% 0% 0%)' }
            ],
            { duration, easing }
          );

          /* Old panel masks out from bottom simultaneously */
          this._leaveAnim = oldPanel.animate(
            [
              { clipPath: 'inset(0% 0% 0% 0%)' },
              { clipPath: 'inset(0% 0% 100% 0%)' }
            ],
            { duration, easing }
          );

          this._revealAnim.onfinish = () => {
            newPanel.style.removeProperty('clip-path');
            oldPanel.classList.remove('is-leaving');
            this._revealAnim = null;
            this._leaveAnim = null;
          };
        } else {
          oldPanel.classList.remove('is-leaving');
        }
      }

      const activeBtn = items[next];
      const url = activeBtn?.getAttribute('data-shop-url') || '';
      if (this._cta) {
        if (url) {
          this._cta.setAttribute('href', url);
          this._cta.removeAttribute('aria-disabled');
        } else {
          this._cta.setAttribute('href', '#');
          this._cta.setAttribute('aria-disabled', 'true');
        }
        // The GA4 payload has to travel with the href. select_item reads
        // data-gtm-item off the clicked anchor at CLICK time
        // (assets/datalayer-tracking.js), so leaving the previous tab's payload
        // in place would report the wrong product for the tab now on screen.
        // Tabs pointing at a collection carry no payload — remove it rather than
        // writing an empty attribute, which would parse-fail on click.
        const gtmItem = activeBtn?.getAttribute('data-shop-gtm-item') || '';
        if (gtmItem) {
          this._cta.setAttribute('data-gtm-item', gtmItem);
        } else {
          this._cta.removeAttribute('data-gtm-item');
        }
      }

      this._updateCtaText();
      this._alignActiveItem(animateAlign);
    }

    /**
     * Keeps the active item vertically centered in the list window by adding the
     * delta between the viewport midline and the item center. No reset-to-zero,
     * so repeated clicks on the same row do not drift.
     */
    _alignActiveItem(animate) {
      const windowEl = this._windowEl;
      const list = this._list;
      const items = this._items();
      const item = items[this._activeIndex];
      if (!windowEl || !list || !item) return;

      const windowRect = windowEl.getBoundingClientRect();
      const lineY = windowRect.top + windowRect.height / 2;
      const itemRect = item.getBoundingClientRect();
      const itemCenterY = itemRect.top + itemRect.height / 2;
      const delta = lineY - itemCenterY;

      if (Math.abs(delta) < 0.5) {
        this._updateLinePosition(item);
        return;
      }

      const matrix = new DOMMatrix(getComputedStyle(list).transform);
      const currentT = Number.isFinite(matrix.m42) ? matrix.m42 : this._listTranslateY;
      const newT = currentT + delta;
      this._listTranslateY = newT;

      if (animate) {
        list.style.removeProperty('transition');
      } else {
        list.style.setProperty('transition', 'none', 'important');
      }
      void list.offsetHeight;
      list.style.transform = `translate3d(0, ${newT}px, 0)`;

      if (!animate) {
        void list.offsetHeight;
        list.style.removeProperty('transition');
        this._updateLinePosition(item);
        this._updateEdgeFades();
      } else {
        requestAnimationFrame(() => this._updateLinePosition(item));
      }
    }

    _recenterActive(animate) {
      if (this._activeIndex < 0) return;
      const items = this._items();
      const item = items[this._activeIndex];
      if (!item) return;

      const windowEl = this._windowEl;
      const list = this._list;
      const windowRect = windowEl.getBoundingClientRect();
      const lineY = windowRect.top + windowRect.height / 2;
      const itemRect = item.getBoundingClientRect();
      const itemCenterY = itemRect.top + itemRect.height / 2;
      const delta = lineY - itemCenterY;

      const matrix = new DOMMatrix(getComputedStyle(list).transform);
      const currentT = Number.isFinite(matrix.m42) ? matrix.m42 : this._listTranslateY;
      this._listTranslateY = currentT + delta;

      if (animate) {
        list.style.removeProperty('transition');
      } else {
        list.style.setProperty('transition', 'none', 'important');
      }
      void list.offsetHeight;
      list.style.transform = `translate3d(0, ${this._listTranslateY}px, 0)`;

      if (!animate) {
        void list.offsetHeight;
        list.style.removeProperty('transition');
        this._updateLinePosition(item);
        this._updateEdgeFades();
      } else {
        requestAnimationFrame(() => this._updateLinePosition(item));
      }
    }

    /**
     * Horizontal rule starts after the active label (inline-end + gap), not through the type.
     * `left` / `right` transitions are defined in CSS.
     */
    _updateLinePosition(item) {
      if (!this._windowEl || !item) return;

      const w = this._windowEl.getBoundingClientRect();
      const ir = item.getBoundingClientRect();
      const isRtl = getComputedStyle(item).direction === 'rtl';

      if (isRtl) {
        this._windowEl.setAttribute('data-line-side', 'rtl');
        this._windowEl.style.setProperty('--cs-line-label-edge', `${w.right - ir.left}px`);
        this._windowEl.style.setProperty('--cs-line-label-start', `${w.right - ir.right}px`);
      } else {
        this._windowEl.setAttribute('data-line-side', 'ltr');
        this._windowEl.style.setProperty('--cs-line-label-edge', `${ir.right - w.left}px`);
        this._windowEl.style.setProperty('--cs-line-label-start', `${ir.left - w.left}px`);
      }

      this._updateEdgeFades();

      if (!this._suppressLineReleased) {
        this._suppressLineReleased = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._windowEl.classList.remove('collections-scroller__list-window--suppress-line-transition');
          });
        });
      }
    }

    /**
     * On mobile, the CTA merges the preview count into the button text:
     * "{count} {name} · {label}". On desktop, it reverts to the plain label.
     */
    _updateCtaText() {
      if (!this._cta) return;
      const items = this._items();
      const active = items[this._activeIndex];
      if (!active) return;

      const label = active.getAttribute('data-cta-label') || this._defaultCtaLabel;

      if (this._mobileQuery.matches) {
        const count = active.getAttribute('data-preview-count') || '0';
        const name = active.getAttribute('data-preview-name') || '';
        if (parseInt(count, 10) > 0 && name) {
          this._cta.textContent = `${count} ${name} \u00B7 ${label}`;
        } else {
          this._cta.textContent = label;
        }
      } else {
        this._cta.textContent = label;
      }
    }

    /**
     * Top/bottom scrims when list content is taller than the clip.
     * Uses scrollHeight vs clientHeight so state does not flicker during translateY
     * (getBoundingClientRect on the list changes every frame while animating).
     */
    _updateEdgeFades() {
      if (!this._windowEl || !this._list) return;

      const windowH = this._windowEl.clientHeight;
      const listH = this._list.scrollHeight;
      const eps = 2;
      const overflowsVertically = listH > windowH + eps;

      this._windowEl.classList.toggle('collections-scroller__list-window--fade-top', overflowsVertically);
      this._windowEl.classList.toggle('collections-scroller__list-window--fade-bottom', overflowsVertically);
    }
  }

  customElements.define('atomic-collections-scroller', AtomicCollectionsScroller);
}
